import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Video, FileText, Phone, Minus, Plus } from 'lucide-react';
import type { CommitmentTaskType } from '@clareefai/types';

interface TaskToggleCardProps {
  type: CommitmentTaskType;
  isSelected: boolean;
  requiredCount: number;
  onToggle: () => void;
  onCountChange: (count: number) => void;
}

const TASK_STYLE: Record<CommitmentTaskType, { icon: React.ComponentType<any>; color: string; bgColor: string }> = {
  REVIEW: { icon: Star, color: 'text-blue-600', bgColor: 'bg-blue-50 border-blue-200' },
  REFERENCE_CALL: { icon: Phone, color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' },
  VIDEO: { icon: Video, color: 'text-purple-600', bgColor: 'bg-purple-50 border-purple-200' },
  USE_CASE: { icon: FileText, color: 'text-orange-600', bgColor: 'bg-orange-50 border-orange-200' },
};

const TaskToggleCard: React.FC<TaskToggleCardProps> = ({ type, isSelected, requiredCount, onToggle, onCountChange }) => {
  const { t } = useTranslation();
  const config = TASK_STYLE[type];
  const Icon = config.icon;

  const TASK_LABELS: Record<CommitmentTaskType, string> = {
    REVIEW: t('rewards.specific.taskCard.review'),
    REFERENCE_CALL: t('rewards.specific.taskCard.referenceCall'),
    VIDEO: t('rewards.specific.taskCard.video'),
    USE_CASE: t('rewards.specific.taskCard.useCase'),
  };

  return (
    <div
      onClick={onToggle}
      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
        isSelected
          ? `${config.bgColor} ring-2 ring-offset-1 ring-[#4339A0]`
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? config.bgColor : 'bg-gray-100'}`}>
          <Icon className={`w-5 h-5 ${isSelected ? config.color : 'text-gray-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-500'}`}>
            {TASK_LABELS[type]}
          </p>
        </div>
        {isSelected && (
          <div className="w-5 h-5 rounded-full bg-[#4339A0] flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
      {isSelected && type === 'REFERENCE_CALL' && (
        <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <span className="text-xs text-gray-500">{t('rewards.specific.taskCard.requiredCalls')}</span>
          <button
            onClick={() => onCountChange(Math.max(1, requiredCount - 1))}
            className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
          >
            <Minus className="w-3 h-3" />
          </button>
          <span className="w-6 text-center text-sm font-medium">{requiredCount}</span>
          <button
            onClick={() => onCountChange(requiredCount + 1)}
            className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TaskToggleCard;
